import React, { useState } from 'react';
import {
  Shield, Search, Filter, Download, CheckCircle, XCircle,
  AlertCircle, FileText, QrCode, Lock, Eye, Calendar, TrendingUp
} from 'lucide-react';

const TaxZATCATab = () => {
  const [activeSubTab, setActiveSubTab] = useState('vat-returns');
  const [searchQuery, setSearchQuery] = useState('');

  const subTabs = [
    { id: 'vat-returns', label: 'VAT Returns' },
    { id: 'invoices', label: 'ZATCA Invoices' },
    { id: 'compliance', label: 'Compliance Status' },
    { id: 'settings', label: 'ZATCA Settings' }
  ];

  const vatReturns = [
    {
      id: 'VAT-2024-Q1',
      period: 'Q1 2024',
      startDate: '2024-01-01',
      endDate: '2024-03-31',
      salesRevenue: 5850000,
      vatOnSales: 975000,
      purchases: 2850000,
      vatOnPurchases: 475000,
      netVAT: 500000,
      status: 'submitted',
      submissionDate: '2024-04-15',
      zatcaStatus: 'cleared'
    },
    {
      id: 'VAT-2023-Q4',
      period: 'Q4 2023',
      startDate: '2023-10-01',
      endDate: '2023-12-31',
      salesRevenue: 5400000,
      vatOnSales: 900000,
      purchases: 2700000,
      vatOnPurchases: 450000,
      netVAT: 450000,
      status: 'submitted',
      submissionDate: '2024-01-15',
      zatcaStatus: 'cleared'
    },
    {
      id: 'VAT-2023-Q3',
      period: 'Q3 2023',
      startDate: '2023-07-01',
      endDate: '2023-09-30',
      salesRevenue: 4950000,
      vatOnSales: 825000,
      purchases: 2550000,
      vatOnPurchases: 425000,
      netVAT: 400000,
      status: 'submitted',
      submissionDate: '2023-10-15',
      zatcaStatus: 'cleared'
    }
  ];

  const zatcaInvoices = [
    {
      id: 'INV-ZATCA-2024-001',
      invoiceNumber: 'INV-2024-001',
      uuid: 'a1b2c3d4-e5f6-7890-abcd-ef1234567890',
      date: '2024-02-11',
      customer: 'Retail Store Chain A',
      amount: 185000,
      vatAmount: 30833.33,
      totalAmount: 215833.33,
      zatcaHash: 'sha256:abc123...',
      zatcaStatus: 'cleared',
      qrCode: 'base64:...',
      digitalSignature: 'ecdsa:...',
      previousHash: 'sha256:xyz789...'
    },
    {
      id: 'INV-ZATCA-2024-002',
      invoiceNumber: 'INV-2024-002',
      uuid: 'b2c3d4e5-f6a7-8901-bcde-f12345678901',
      date: '2024-02-10',
      customer: 'Wholesale Distributor B',
      amount: 125000,
      vatAmount: 20833.33,
      totalAmount: 145833.33,
      zatcaHash: 'sha256:def456...',
      zatcaStatus: 'cleared',
      qrCode: 'base64:...',
      digitalSignature: 'ecdsa:...',
      previousHash: 'sha256:abc123...'
    },
    {
      id: 'INV-ZATCA-2024-003',
      invoiceNumber: 'INV-2024-003',
      uuid: 'c3d4e5f6-a7b8-9012-cdef-123456789012',
      date: '2024-02-09',
      customer: 'Corporate Client C',
      amount: 95000,
      vatAmount: 15833.33,
      totalAmount: 110833.33,
      zatcaHash: 'sha256:ghi789...',
      zatcaStatus: 'pending',
      qrCode: 'base64:...',
      digitalSignature: 'ecdsa:...',
      previousHash: 'sha256:def456...'
    },
    {
      id: 'INV-ZATCA-2024-004',
      invoiceNumber: 'INV-2024-004',
      uuid: 'd4e5f6a7-b8c9-0123-defa-234567890123',
      date: '2024-02-08',
      customer: 'Online Retailer D',
      amount: 65000,
      vatAmount: 10833.33,
      totalAmount: 75833.33,
      zatcaHash: 'sha256:jkl012...',
      zatcaStatus: 'rejected',
      qrCode: 'base64:...',
      digitalSignature: 'ecdsa:...',
      previousHash: 'sha256:ghi789...'
    }
  ];

  const complianceStatus = {
    overall: 'compliant',
    lastAudit: '2024-01-15',
    nextAudit: '2024-04-15',
    issues: 0,
    warnings: 2,
    checks: [
      { name: 'Invoice Numbering', status: 'pass', lastCheck: '2024-02-11' },
      { name: 'Hash Chaining', status: 'pass', lastCheck: '2024-02-11' },
      { name: 'Digital Signatures', status: 'pass', lastCheck: '2024-02-11' },
      { name: 'QR Code Generation', status: 'warning', lastCheck: '2024-02-10' },
      { name: 'UBL 2.1 Format', status: 'pass', lastCheck: '2024-02-11' },
      { name: 'VAT Calculation', status: 'pass', lastCheck: '2024-02-11' },
      { name: 'Submission Timing', status: 'warning', lastCheck: '2024-02-09' }
    ]
  };

  const getZATCAStatusBadge = (status) => {
    switch (status) {
      case 'cleared':
        return <span className="px-2 py-1 text-xs font-medium rounded bg-green-100 text-green-700 flex items-center gap-1">
          <CheckCircle className="h-3 w-3" />
          Cleared
        </span>;
      case 'pending':
        return <span className="px-2 py-1 text-xs font-medium rounded bg-yellow-100 text-yellow-700 flex items-center gap-1">
          <AlertCircle className="h-3 w-3" />
          Pending
        </span>;
      case 'rejected':
        return <span className="px-2 py-1 text-xs font-medium rounded bg-red-100 text-red-700 flex items-center gap-1">
          <XCircle className="h-3 w-3" />
          Rejected
        </span>;
      default:
        return <span className="px-2 py-1 text-xs font-medium rounded bg-gray-100 text-gray-700">Unknown</span>;
    }
  };

  const getCheckStatusBadge = (status) => {
    switch (status) {
      case 'pass':
        return <span className="px-2 py-1 text-xs font-medium rounded bg-green-100 text-green-700">Pass</span>;
      case 'warning':
        return <span className="px-2 py-1 text-xs font-medium rounded bg-yellow-100 text-yellow-700">Warning</span>;
      case 'fail':
        return <span className="px-2 py-1 text-xs font-medium rounded bg-red-100 text-red-700">Fail</span>;
      default:
        return <span className="px-2 py-1 text-xs font-medium rounded bg-gray-100 text-gray-700">Unknown</span>;
    }
  };

  return (
    <div className="space-y-6">
      {/* Sub-tabs */}
      <div className="flex items-center gap-2 border-b border-gray-200">
        {subTabs.map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveSubTab(tab.id)}
            className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
              activeSubTab === tab.id
                ? 'border-blue-600 text-blue-600'
                : 'border-transparent text-gray-600 hover:text-gray-900'
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* VAT Returns */}
      {activeSubTab === 'vat-returns' && (
        <div className="space-y-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search returns..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </div>
            <div className="flex items-center gap-3">
              <button className="px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium hover:bg-gray-50 flex items-center gap-2">
                <Download className="h-4 w-4" />
                Export
              </button>
              <button className="px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 flex items-center gap-2">
                <FileText className="h-4 w-4" />
                File Return
              </button>
            </div>
          </div>

          <div className="bg-white rounded-xl border border-gray-200 shadow-sm">
            <div className="p-5 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900">VAT Returns History</h3>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-xs font-semibold text-gray-600 uppercase border-b border-gray-200 bg-gray-50">
                    <th className="text-left py-3 px-4">Period</th>
                    <th className="text-left py-3 px-4">Date Range</th>
                    <th className="text-right py-3 px-4">Sales Revenue</th>
                    <th className="text-right py-3 px-4">VAT on Sales</th>
                    <th className="text-right py-3 px-4">Purchases</th>
                    <th className="text-right py-3 px-4">VAT on Purchases</th>
                    <th className="text-right py-3 px-4">Net VAT</th>
                    <th className="text-center py-3 px-4">Status</th>
                    <th className="text-left py-3 px-4">Submission Date</th>
                    <th className="text-center py-3 px-4">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {vatReturns.map((return_) => (
                    <tr key={return_.id} className="border-b border-gray-200 hover:bg-gray-50">
                      <td className="py-3 px-4 font-medium text-gray-900">{return_.period}</td>
                      <td className="py-3 px-4 text-gray-600">
                        {return_.startDate} to {return_.endDate}
                      </td>
                      <td className="py-3 px-4 text-right text-gray-900">
                        SAR {return_.salesRevenue.toLocaleString()}
                      </td>
                      <td className="py-3 px-4 text-right text-gray-900">
                        SAR {return_.vatOnSales.toLocaleString()}
                      </td>
                      <td className="py-3 px-4 text-right text-gray-900">
                        SAR {return_.purchases.toLocaleString()}
                      </td>
                      <td className="py-3 px-4 text-right text-gray-900">
                        SAR {return_.vatOnPurchases.toLocaleString()}
                      </td>
                      <td className="py-3 px-4 text-right font-semibold text-blue-600">
                        SAR {return_.netVAT.toLocaleString()}
                      </td>
                      <td className="py-3 px-4">{getZATCAStatusBadge(return_.zatcaStatus)}</td>
                      <td className="py-3 px-4 text-gray-600">{return_.submissionDate}</td>
                      <td className="py-3 px-4">
                        <div className="flex items-center justify-center gap-2">
                          <button className="p-1.5 hover:bg-gray-200 rounded">
                            <Eye className="h-4 w-4 text-gray-600" />
                          </button>
                          <button className="p-1.5 hover:bg-gray-200 rounded">
                            <Download className="h-4 w-4 text-gray-600" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* ZATCA Invoices */}
      {activeSubTab === 'invoices' && (
        <div className="space-y-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search invoices..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <select className="px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium bg-white">
                <option value="all">All Status</option>
                <option value="cleared">Cleared</option>
                <option value="pending">Pending</option>
                <option value="rejected">Rejected</option>
              </select>
            </div>
            <div className="flex items-center gap-3">
              <button className="px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium hover:bg-gray-50 flex items-center gap-2">
                <Download className="h-4 w-4" />
                Export
              </button>
            </div>
          </div>

          <div className="bg-white rounded-xl border border-gray-200 shadow-sm">
            <div className="p-5 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900">ZATCA Compliant Invoices</h3>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-xs font-semibold text-gray-600 uppercase border-b border-gray-200 bg-gray-50">
                    <th className="text-left py-3 px-4">Invoice #</th>
                    <th className="text-left py-3 px-4">UUID</th>
                    <th className="text-left py-3 px-4">Date</th>
                    <th className="text-left py-3 px-4">Customer</th>
                    <th className="text-right py-3 px-4">Amount</th>
                    <th className="text-right py-3 px-4">VAT</th>
                    <th className="text-right py-3 px-4">Total</th>
                    <th className="text-center py-3 px-4">ZATCA Status</th>
                    <th className="text-center py-3 px-4">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {zatcaInvoices.map((invoice) => (
                    <tr key={invoice.id} className="border-b border-gray-200 hover:bg-gray-50">
                      <td className="py-3 px-4">
                        <div>
                          <p className="font-medium text-gray-900">{invoice.invoiceNumber}</p>
                          <p className="text-xs text-gray-500">{invoice.id}</p>
                        </div>
                      </td>
                      <td className="py-3 px-4">
                        <code className="text-xs text-gray-600 font-mono">{invoice.uuid.substring(0, 20)}...</code>
                      </td>
                      <td className="py-3 px-4 text-gray-600">{invoice.date}</td>
                      <td className="py-3 px-4 text-gray-900">{invoice.customer}</td>
                      <td className="py-3 px-4 text-right text-gray-900">
                        SAR {invoice.amount.toLocaleString()}
                      </td>
                      <td className="py-3 px-4 text-right text-gray-900">
                        SAR {invoice.vatAmount.toLocaleString()}
                      </td>
                      <td className="py-3 px-4 text-right font-semibold text-gray-900">
                        SAR {invoice.totalAmount.toLocaleString()}
                      </td>
                      <td className="py-3 px-4">{getZATCAStatusBadge(invoice.zatcaStatus)}</td>
                      <td className="py-3 px-4">
                        <div className="flex items-center justify-center gap-2">
                          <button className="p-1.5 hover:bg-gray-200 rounded" title="View Details">
                            <Eye className="h-4 w-4 text-gray-600" />
                          </button>
                          <button className="p-1.5 hover:bg-gray-200 rounded" title="View QR Code">
                            <QrCode className="h-4 w-4 text-gray-600" />
                          </button>
                          <button className="p-1.5 hover:bg-gray-200 rounded" title="Download UBL">
                            <Download className="h-4 w-4 text-gray-600" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* Compliance Status */}
      {activeSubTab === 'compliance' && (
        <div className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="bg-white rounded-xl p-5 border border-gray-200 shadow-sm">
              <div className="flex items-center justify-between mb-2">
                <p className="text-sm font-medium text-gray-600">Overall Status</p>
                <Shield className="h-5 w-5 text-green-600" />
              </div>
              <p className="text-2xl font-bold text-green-600 capitalize">{complianceStatus.overall}</p>
            </div>
            <div className="bg-white rounded-xl p-5 border border-gray-200 shadow-sm">
              <div className="flex items-center justify-between mb-2">
                <p className="text-sm font-medium text-gray-600">Issues</p>
                <XCircle className="h-5 w-5 text-red-600" />
              </div>
              <p className="text-2xl font-bold text-red-600">{complianceStatus.issues}</p>
            </div>
            <div className="bg-white rounded-xl p-5 border border-gray-200 shadow-sm">
              <div className="flex items-center justify-between mb-2">
                <p className="text-sm font-medium text-gray-600">Warnings</p>
                <AlertCircle className="h-5 w-5 text-yellow-600" />
              </div>
              <p className="text-2xl font-bold text-yellow-600">{complianceStatus.warnings}</p>
            </div>
            <div className="bg-white rounded-xl p-5 border border-gray-200 shadow-sm">
              <div className="flex items-center justify-between mb-2">
                <p className="text-sm font-medium text-gray-600">Next Audit</p>
                <Calendar className="h-5 w-5 text-blue-600" />
              </div>
              <p className="text-lg font-bold text-gray-900">{complianceStatus.nextAudit}</p>
            </div>
          </div>

          <div className="bg-white rounded-xl border border-gray-200 shadow-sm">
            <div className="p-5 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900">Compliance Checks</h3>
            </div>
            <div className="divide-y divide-gray-200">
              {complianceStatus.checks.map((check, index) => (
                <div key={index} className="p-5 flex items-center justify-between hover:bg-gray-50">
                  <div>
                    <p className="font-medium text-gray-900">{check.name}</p>
                    <p className="text-xs text-gray-500 mt-1">Last checked: {check.lastCheck}</p>
                  </div>
                  <div className="flex items-center gap-3">
                    {getCheckStatusBadge(check.status)}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* ZATCA Settings */}
      {activeSubTab === 'settings' && (
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">ZATCA Configuration</h3>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">VAT Registration Number</label>
              <input
                type="text"
                defaultValue="123456789012345"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Company Name</label>
              <input
                type="text"
                defaultValue="Lyncs Company"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">ZATCA API Endpoint</label>
              <input
                type="text"
                defaultValue="https://zatca-api.sa/v2"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Private Key (ECDSA)</label>
              <textarea
                rows={4}
                defaultValue="-----BEGIN PRIVATE KEY-----..."
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 font-mono text-xs"
              />
            </div>
            <div className="flex items-center gap-2">
              <input type="checkbox" id="auto-submit" defaultChecked className="rounded" />
              <label htmlFor="auto-submit" className="text-sm text-gray-700">Auto-submit invoices to ZATCA</label>
            </div>
            <div className="flex items-center gap-2">
              <input type="checkbox" id="hash-chaining" defaultChecked className="rounded" />
              <label htmlFor="hash-chaining" className="text-sm text-gray-700">Enable hash chaining</label>
            </div>
            <button className="px-6 py-2 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700">
              Save Settings
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default TaxZATCATab;
